import { useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ptBR } from "date-fns/locale";

interface DatePicker18Props {
  value: string;
  onChange: (date: string) => void;
}

function formatDateBR(date: Date) {
  return date.toLocaleDateString("pt-BR");
}

function parseDateBR(str: string): Date | null {
  const [d, m, y] = str.split("/");
  if (!d || !m || !y) return null;
  const date = new Date(+y, +m - 1, +d);
  return isNaN(date.getTime()) ? null : date;
}

export default function DatePicker18({ value, onChange }: DatePicker18Props) {
  const [show, setShow] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Limite: só pode selecionar datas até 18 anos atrás
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

  // Sincroniza o valor externo (ISO) com o input (BR)
  useEffect(() => {
    if (value) {
      const [year, month, day] = value.split('-');
      setInputValue(formatDateBR(new Date(Number(year), Number(month) - 1, Number(day))));
    } else {
      setInputValue("");
    }
  }, [value]);

  // Fecha o calendário ao clicar fora do input e do popover
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        inputRef.current &&
        !inputRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setShow(false);
      }
    }
    if (show) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [show]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    let val = e.target.value.replace(/\D/g, ""); // Remove tudo que não for número
    if (val.length > 8) val = val.slice(0, 8);
    let masked = val;
    if (val.length > 4) masked = val.slice(0,2) + "/" + val.slice(2,4) + "/" + val.slice(4);
    else if (val.length > 2) masked = val.slice(0,2) + "/" + val.slice(2);
    setInputValue(masked);

    if (masked.length === 10) {
      const date = parseDateBR(masked);
      if (date && date <= maxDate) {
        onChange(date.toISOString().slice(0, 10));
      }
    }
  }

  // Para manter o mês/ano selecionado ao reabrir
  const selectedDate = value ? (() => {
    const [year, month, day] = value.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
  })() : undefined;

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShow(true)}
        placeholder="dd/mm/aaaa"
        className="bg-protec-dark border-protec-gold/30 text-white w-full px-4 py-2 rounded"
        maxLength={10}
      />
      {show && (
        <div
          ref={popoverRef}
          className="absolute bottom-full mb-2 left-0 z-50 bg-protec-dark-light p-1 rounded shadow-lg"
          style={{ minWidth: inputRef.current?.offsetWidth || 180, maxWidth: 220 }}
        >
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={date => {
              if (date) {
                const iso = date.toLocaleDateString('en-CA');
                onChange(iso);
                setInputValue(formatDateBR(date));
                setShow(false);
              }
            }}
            defaultMonth={selectedDate}
            toDate={maxDate}
            fromYear={1900}
            toYear={maxDate.getFullYear()}
            locale={ptBR}
            captionLayout="dropdown"
            className="!bg-protec-dark-light text-gray-300"
            // classNames removido para evitar duplicação do cabeçalho
          />
        </div>
      )}
    </div>
  );
} 